import React, {useEffect, useMemo, useState} from 'react';
import {Prompt, useHistory, useLocation, useParams} from "react-router-dom";
import {useSelector} from "react-redux";
import {CAlert, CButton, CInput, CSwitch} from '@coreui/react';
import CIcon from "@coreui/icons-react";
import {getApi} from "../../apis/backendApis";
import useLoading from "../../services/useLoading";
import useAlert from "../../services/useAlert";
import {apiNegada} from "../../services/acesso";
import {States} from "../../store/ducks/rootReducer";
import {Etapa, Fluxo, Item, Modulo, Papel, Selecao, TipoPapel, paraApi, paraTela} from "./modelo";
import Estrutura from "./estrutura";
import PapeisTab from "./papeisTab";
import ModulosTab from "./modulosTab";
import PreviaModal from "./previaModal";
import '../../scss/styles-fluxos.scss';

type Modo = 'incluir' | 'alterar' | 'visualizar' | 'duplicar';
type Aba = 'estrutura' | 'papeis' | 'modulos';

interface Params {
    id?: string
}

const fluxoVazio = (): Fluxo => ({
    codigo: '',
    nome: '',
    descricao: null,
    ativo: true,
    setores: [],
    papeis: [{codigo: 'GP', nome: 'Gerente de Projetos', tipo: TipoPapel.INTERNO, setor: null, usuario: null}],
    modulos: [],
    etapas: [],
});

/** Copia sem ids: salvar cria tudo de novo. */
const semIds = (fluxo: Fluxo): Fluxo => ({
    ...fluxo,
    id: undefined,
    versao: undefined,
    data_alteracao: null,
    usuario_alteracao: null,
    papeis: fluxo.papeis.map(papel => ({...papel, id: undefined})),
    modulos: fluxo.modulos.map(modulo => ({...modulo, id: undefined})),
    etapas: fluxo.etapas.map(etapa => ({
        ...etapa,
        id: undefined,
        grupos: etapa.grupos.map(grupo => ({...grupo, id: undefined})),
        itens: etapa.itens.map(item => ({...item, id: undefined})),
    })),
});

/** "Corrija o fluxo antes de salvar:\n- a\n- b" -> ['a', 'b'] */
const linhasDoErro = (erro: string): string[] => {
    const partes = erro.split('\n- ');
    return partes.length > 1 ? partes.slice(1) : [erro];
};

const FluxoEditor = () => {
    const {id} = useParams<Params>();
    const location = useLocation();
    const history = useHistory();
    const [setLoading] = useLoading();
    const [showAlert] = useAlert();
    const acesso = useSelector((state: States) => state.login.data?.acesso);

    const modo: Modo = useMemo(() => {
        const partes = location.pathname.split('/');
        if (partes.includes('alterar')) return 'alterar';
        if (partes.includes('visualizar')) return 'visualizar';
        if (partes.includes('duplicar')) return 'duplicar';
        return 'incluir';
    }, [location.pathname]);

    const [fluxo, setFluxo] = useState<Fluxo>(fluxoVazio());
    const [carregado, setCarregado] = useState(!id);
    const [alterado, setAlterado] = useState(false);
    const [erros, setErros] = useState<string[]>([]);
    const [aba, setAba] = useState<Aba>('estrutura');
    const [selecao, setSelecao] = useState<Selecao | null>(null);
    const [previaAberta, setPreviaAberta] = useState(false);

    const rotaSalvar = modo === 'alterar' ? `/fluxo/edit/${id}` : '/fluxo/add';
    const somenteLeitura = modo === 'visualizar' || apiNegada(acesso, 'POST', rotaSalvar);

    const carregar = () => {
        if (!id) {
            return;
        }
        setLoading(true);
        getApi().get(`fluxo/get/${id}`).then(resposta => {
            let dados = paraTela(resposta.data);
            if (modo === 'duplicar') {
                dados = {...semIds(dados), codigo: '', nome: `Cópia de ${dados.nome}`};
            }
            setFluxo(dados);
            setAlterado(modo === 'duplicar');
            setCarregado(true);
        }).catch(erro => {
            showAlert({title: 'Erro', msg: erro?.response?.data?.error ?? 'Não foi possível carregar o fluxo.'});
        }).finally(() => setLoading(false));
    };

    useEffect(carregar, [id, modo]);

    // Aviso do navegador ao fechar a aba com alteracao pendente.
    useEffect(() => {
        if (!alterado) {
            return;
        }
        const aviso = (evento: BeforeUnloadEvent) => {
            evento.preventDefault();
            evento.returnValue = '';
        };
        window.addEventListener('beforeunload', aviso);
        return () => window.removeEventListener('beforeunload', aviso);
    }, [alterado]);

    const muda = (novo: Fluxo) => {
        setFluxo(novo);
        setAlterado(true);
    };

    const mudaCabecalho = (campo: keyof Fluxo, valor: any) => muda({...fluxo, [campo]: valor});

    // Codigo de papel, modulo e grupo e a referencia dos itens: renomear leva junto.
    const mudaPapeis = (papeis: Papel[], renomeado?: {de: string, para: string}) => {
        let etapas = fluxo.etapas;
        if (renomeado) {
            const troca = (codigo: string) => (codigo === renomeado.de ? renomeado.para : codigo);
            etapas = etapas.map(etapa => ({
                ...etapa,
                papel: troca(etapa.papel),
                itens: etapa.itens.map(item => ({...item, papel: troca(item.papel)})),
            }));
        }
        muda({...fluxo, papeis, etapas});
    };

    const mudaModulos = (modulos: Modulo[], renomeado?: {de: string, para: string}) => {
        let etapas = fluxo.etapas;
        if (renomeado) {
            etapas = etapas.map(etapa => ({
                ...etapa,
                itens: etapa.itens.map(item => (item.modulo === renomeado.de ? {...item, modulo: renomeado.para} : item)),
            }));
        }
        muda({...fluxo, modulos, etapas});
    };

    const mudaEtapas = (etapas: Etapa[]) => muda({...fluxo, etapas});

    const salvar = () => {
        setErros([]);
        const corpo = new FormData();
        corpo.append('data', JSON.stringify(paraApi(fluxo)));
        setLoading(true);
        getApi().post(rotaSalvar, corpo).then(resposta => {
            showAlert({msg: resposta.data.msg, type: 'success', toaster: true});
            setAlterado(false);
            if (modo === 'alterar') {
                carregar();
            } else {
                // O Prompt le "alterado" no proximo render: navega depois dele.
                setTimeout(() => history.replace(`/fluxos/alterar/${resposta.data.data.id}`), 0);
            }
        }).catch(erro => {
            const mensagem = String(erro?.response?.data?.error ?? 'Não foi possível salvar o fluxo.');
            setErros(linhasDoErro(mensagem));
            window.scrollTo({top: 0, behavior: 'smooth'});
        }).finally(() => setLoading(false));
    };

    const titulo = {
        incluir: 'Incluir fluxo',
        alterar: 'Alterar fluxo',
        visualizar: 'Visualizar fluxo',
        duplicar: 'Duplicar fluxo',
    }[modo];

    const resumo = useMemo(() => {
        const ativas = fluxo.etapas.filter(etapa => etapa.ativo);
        const itens = ativas.reduce((lista: Item[], etapa) => lista.concat(etapa.itens.filter(item => item.ativo)), []);
        const papeisCliente = new Set(fluxo.papeis.filter(p => p.tipo === TipoPapel.CLIENTE).map(p => p.codigo));
        return {
            etapas: ativas.length,
            itens: itens.length,
            cliente: itens.filter(item => item.acao_cliente || papeisCliente.has(item.papel)).length,
        };
    }, [fluxo]);

    if (!carregado) {
        return null;
    }

    const abas: Array<[Aba, string, number | null]> = [
        ['estrutura', 'Estrutura', null],
        ['papeis', 'Papéis', fluxo.papeis.length],
        ['modulos', 'Módulos', fluxo.modulos.length],
    ];

    return (
        <div className="fx">
            <Prompt when={alterado} message="Há alterações não salvas no fluxo. Sair mesmo assim?"/>

            <header className="fx-topo">
                <div className="fx-topo-linha">
                    <button type="button" className="fx-voltar" onClick={() => history.push('/fluxos')} title="Voltar para a lista">
                        <CIcon name="cil-arrow-left"/>
                    </button>
                    <div className="fx-campos">
                        <label className="fx-campo fx-campo-codigo">
                            <span>Código</span>
                            <CInput id="codigo" value={fluxo.codigo} maxLength={20} disabled={somenteLeitura} placeholder="ONB"
                                    onChange={(e: any) => mudaCabecalho('codigo', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}/>
                        </label>
                        <label className="fx-campo fx-campo-nome">
                            <span>Nome do fluxo</span>
                            <CInput id="nome" value={fluxo.nome} maxLength={120} disabled={somenteLeitura} placeholder="Onboarding de Clientes"
                                    onChange={(e: any) => mudaCabecalho('nome', e.target.value)}/>
                        </label>
                        <label className="fx-campo fx-campo-descricao">
                            <span>Descrição</span>
                            <CInput id="descricao" value={fluxo.descricao || ''} disabled={somenteLeitura}
                                    onChange={(e: any) => mudaCabecalho('descricao', e.target.value)}/>
                        </label>
                        <div className="fx-campo fx-campo-ativo">
                            <span>Situação</span>
                            <div className="fx-ativo">
                                <CSwitch color="success" shape="pill" size="sm" checked={fluxo.ativo} disabled={somenteLeitura}
                                         onChange={(e: any) => mudaCabecalho('ativo', e.target.checked)}/>
                                {fluxo.ativo ? 'Ativo' : 'Inativo'}
                            </div>
                        </div>
                    </div>
                    <div className="fx-acoes">
                        <CButton size="sm" color="info" variant="outline" onClick={() => setPreviaAberta(true)}>
                            <CIcon name="cil-calendar"/> Prévia do cronograma
                        </CButton>
                        {!somenteLeitura && (
                            <CButton size="sm" color="primary" onClick={salvar} className={alterado ? 'fx-pendente' : ''}>
                                <CIcon name="cil-save"/> {alterado ? 'Salvar alterações' : 'Salvar'}
                            </CButton>
                        )}
                    </div>
                </div>

                <div className="fx-topo-linha fx-topo-abas">
                    <nav className="fx-abas">
                        {abas.map(([chave, rotulo, total]) => (
                            <button type="button" key={chave} className={aba === chave ? 'ativa' : ''} onClick={() => setAba(chave)}>
                                {rotulo}{total !== null && <span className="fx-contador">{total}</span>}
                            </button>
                        ))}
                    </nav>
                    <div className="fx-resumo">
                        <span><strong>{resumo.etapas}</strong> etapas</span>
                        <span><strong>{resumo.itens}</strong> itens</span>
                        <span><strong>{resumo.cliente}</strong> envolvem o cliente</span>
                        <span className="fx-titulo-modo">
                            {titulo}
                            {fluxo.versao && ` · versão ${fluxo.versao}`}
                            {fluxo.usuario_alteracao && ` · ${fluxo.usuario_alteracao}`}
                            {fluxo.data_alteracao && ` em ${fluxo.data_alteracao.split(' ')[0].split('-').reverse().join('/')}`}
                        </span>
                    </div>
                </div>
            </header>

            {erros.length > 0 && (
                // onShowChange tambem dispara ao montar (com true): so limpa ao fechar.
                <CAlert color="danger" closeButton className="fx-erros" onShowChange={(visivel: any) => !visivel && setErros([])}>
                    <strong>Corrija o fluxo antes de salvar:</strong>
                    <ul>
                        {erros.map((linha, i) => <li key={i}>{linha}</li>)}
                    </ul>
                </CAlert>
            )}

            <div className="fx-corpo">
                {aba === 'estrutura' && (
                    <Estrutura fluxo={fluxo} selecao={selecao} somenteLeitura={somenteLeitura}
                               onSelecionar={setSelecao} onMudarEtapas={mudaEtapas}/>
                )}
                {aba === 'papeis' && (
                    <PapeisTab fluxo={fluxo} somenteLeitura={somenteLeitura} onMudar={mudaPapeis}
                               onMudarSetores={(setores, papeis) => muda({...fluxo, setores, papeis})}/>
                )}
                {aba === 'modulos' && (
                    <ModulosTab fluxo={fluxo} somenteLeitura={somenteLeitura} onMudar={mudaModulos}/>
                )}
            </div>

            {previaAberta && (
                <PreviaModal fluxo={fluxo} onFechar={() => setPreviaAberta(false)}/>
            )}
        </div>
    );
};

export default FluxoEditor;
